import React from 'react';
import PropTypes from 'prop-types';
import format from 'string-template';
import { Check, Smartphone } from 'lucide-react';

import config from '../../../config';
import getAppStores from '../../../utils/appStores';
import { getScheduleLine } from '../core/shareCard';
import texts from '../texts.json';
import { StoryOutcome } from './DoneStep';
import {
  Actions,
  AppBadge,
  AppIntro,
  AppIntroText,
  Body,
  CheckBadge,
  Checklist,
  ChecklistItem,
  ChecklistText,
  EmptyBadge,
  Footnote,
  PrimaryLink,
  SecondaryLink,
  StickerImage,
  Title,
} from './styles';

const STORES = {
  ios: { label: texts.app.appStore, url: config.storeURLs.ios },
  android: { label: texts.app.googlePlay, url: config.storeURLs.android },
};

const Done = () => (
  <CheckBadge $size={26}><Check size={14} strokeWidth={3.2} aria-hidden="true" /></CheckBadge>
);

/*
  Where Task 1 ends, however the client left it: what they have done, and the app, which is where the challenge
  happens.
*/
const GetAppStep = ({
  card,
  outcome,
  stickerUrl,
  footer,
}) => {
  const copy = texts.app[outcome];
  const [primaryStore, secondaryStore] = getAppStores().map((store) => STORES[store]);

  return (
    <>
      <AppIntro>
        {!!stickerUrl && <StickerImage src={stickerUrl} alt={texts.done.sticker} $width={100} $tilt={-5} />}
        <AppIntroText>
          <Title $size={29}>{copy.title}</Title>
          <Body>{copy.body}</Body>
        </AppIntroText>
      </AppIntro>
      <Checklist>
        <ChecklistItem>
          <Done />
          <ChecklistText>
            <strong>{format(texts.app.joined, { challengeName: card.challengeName })}</strong>
            {getScheduleLine(card, new Date())}
          </ChecklistText>
        </ChecklistItem>
        <ChecklistItem>
          {stickerUrl ? <Done /> : <EmptyBadge $size={26} />}
          <ChecklistText>
            <strong>{texts.app.task}</strong>
            {stickerUrl ? copy.task : texts.app.taskSkipped}
          </ChecklistText>
        </ChecklistItem>
        <ChecklistItem>
          <AppBadge><Smartphone size={15} aria-hidden="true" /></AppBadge>
          <ChecklistText>
            <strong>{texts.app.getApp}</strong>
            {format(texts.app.getAppBody, { coachName: card.coachName })}
          </ChecklistText>
        </ChecklistItem>
      </Checklist>
      <Actions>
        <PrimaryLink href={primaryStore.url} target="_blank" rel="noopener noreferrer">
          {primaryStore.label}
        </PrimaryLink>
        {!!secondaryStore && (
          <SecondaryLink href={secondaryStore.url} target="_blank" rel="noopener noreferrer">
            {secondaryStore.label}
          </SecondaryLink>
        )}
        <Footnote>{texts.app.logIn}</Footnote>
      </Actions>
      {footer}
    </>
  );
};

GetAppStep.propTypes = {
  card: PropTypes.shape({
    challengeName: PropTypes.string.isRequired,
    coachName: PropTypes.string.isRequired,
  }).isRequired,
  outcome: PropTypes.oneOf(Object.values(StoryOutcome)).isRequired,
  // The sticker the client made, or null when they skipped making one.
  stickerUrl: PropTypes.string,
  // What the page adds under the store buttons, such as the email the account is linked to.
  footer: PropTypes.node,
};

GetAppStep.defaultProps = {
  stickerUrl: null,
  footer: null,
};

export default GetAppStep;
